import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { faBoxArchive } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { archiveCustomField } from 'api/customFields';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';
import { customFieldQueryKeys } from 'queries/custom-field-query-keys';
import { CustomField } from 'types/domain';

interface ArchiveCustomFieldDialogProps extends AlertDialog.AlertDialogRootProps {
    customField: CustomField;
    onSuccess?: () => void;
}

const ArchiveCustomFieldDialog = (props: ArchiveCustomFieldDialogProps) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { mutate: invalidateUserData } = useInvalidateUserAndAccount();
    const { customField, onSuccess, children, ...rest } = props;

    const { mutate: onArchiveCustomField } = useMutation({
        mutationFn: () => archiveCustomField(accountId, customField.id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: customFieldQueryKeys.customFields(accountId),
            });
            invalidateUserData();
            onSuccess?.();
            toast.success(
                <p>
                    <span className={'font-medium'}>{customField.title}</span> was archived.
                </p>,
            );
        },
    });

    return (
        <AlertDialog.Root type={'error'} {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header className={'!items-start pb-4'}>
                        <div
                            className={
                                'relative flex aspect-square h-8 items-center justify-center rounded-full bg-red-500 text-white after:absolute after:-inset-1 after:-z-[1] after:rounded-full after:bg-red-100 after:p-2'
                            }
                        >
                            <FontAwesomeIcon icon={faBoxArchive} className={'text-lg text-white'} />
                        </div>
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title className={'text-xl'}>
                            Archive custom field?
                        </AlertDialog.Title>
                        <AlertDialog.Description>
                            This action will archive <b>{customField.title}</b> and no longer
                            display the field or existing responses anywhere.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer className={'gap-x-4'}>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => onArchiveCustomField()}>
                            Archive
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const ArchiveCustomFieldDialogTrigger = AlertDialog.Trigger;

const Root = ArchiveCustomFieldDialog;
const Trigger = ArchiveCustomFieldDialogTrigger;

export {
    ArchiveCustomFieldDialog,
    ArchiveCustomFieldDialogTrigger,
    //
    Root,
    Trigger,
};
